// Signage → Buzón externo (/signage/buzon). Carpetas compartidas (Google Drive /
// Dropbox) donde agencias y proveedores depositan imágenes/videos. El worker las
// vigila y lo NUEVO cae aquí en MODERACIÓN: nada se publica sin la aprobación de
// un admin. Imagen aprobada → Biblioteca de medios al instante; video aprobado →
// el worker lo mueve al hosting en su próximo ciclo.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, FileVideo, Image as ImageIcon, Inbox, KeyRound, Pencil, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Modal, Switch, fieldCls } from '@/features/pos/shared';
import { sigInsert, sigList, sigRpc, sigUpdate } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Fuente {
  id: string; proveedor: 'gdrive' | 'dropbox'; nombre: string; carpeta: string;
  credencial: string | null; intervalo_min: number; activo: boolean;
  ultima_sync: string | null; ultimo_error: string | null;
}
interface Item {
  id: string; fuente_id: string; nombre: string; tipo: 'imagen' | 'video'; mime: string | null;
  bytes: number | null; datos: string | null; estado: string; error: string | null;
  created_at: string; decidido_at: string | null;
}

const PROV = { gdrive: 'Google Drive', dropbox: 'Dropbox' } as const;
const ESTADO_CLS: Record<string, string> = {
  pendiente: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  aprobado: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  publicado: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  rechazado: 'bg-muted text-muted-foreground',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

export function SignageBuzon() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [edit, setEdit] = useState<Partial<Fuente> | null>(null);
  const [credDe, setCredDe] = useState<Fuente | null>(null);
  const [cred, setCred] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [filtro, setFiltro] = useState('pendiente');

  const { data: fuentes = [] } = useQuery<Fuente[]>({
    queryKey: ['sig-buzon-fuentes'],
    queryFn: () => sigList<Fuente>('buzon_fuente?select=*&deleted_at=is.null&order=nombre'),
    staleTime: 0,
  });
  const { data: items = [] } = useQuery<Item[]>({
    queryKey: ['sig-buzon-items', filtro],
    queryFn: () => sigList<Item>(`buzon_item?select=*&deleted_at=is.null${filtro ? `&estado=eq.${filtro}` : ''}&order=created_at.desc&limit=60`),
    staleTime: 0, refetchInterval: 30_000,
  });
  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['sig-buzon-fuentes'] });
    void qc.invalidateQueries({ queryKey: ['sig-buzon-items'] });
  };

  const guardarFuente = async () => {
    if (!edit?.nombre?.trim() || !edit.carpeta?.trim()) { toast.error('Nombre y carpeta son obligatorios'); return; }
    setGuardando(true);
    try {
      const body = {
        proveedor: edit.proveedor ?? 'gdrive', nombre: edit.nombre.trim(), carpeta: edit.carpeta.trim(),
        intervalo_min: edit.intervalo_min ?? 10, activo: edit.activo ?? true,
      };
      if (edit.id) await sigUpdate('buzon_fuente', edit.id, body); else await sigInsert('buzon_fuente', body);
      setEdit(null); invalidar();
      toast.success('Fuente guardada — recuerda cargar la credencial (🔑)');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGuardando(false); }
  };

  const guardarCred = async () => {
    if (!credDe) return;
    setGuardando(true);
    try {
      await sigRpc('sig_buzon_credencial_set', { _fuente: credDe.id, _credencial: cred });
      setCredDe(null); setCred(''); invalidar();
      toast.success('Credencial guardada cifrada — el worker sincroniza en el próximo ciclo');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGuardando(false); }
  };

  const decidir = async (item: Item, aprobar: boolean) => {
    try {
      const r = await sigRpc<{ estado: string }>('sig_buzon_decidir', { _item: item.id, _aprobar: aprobar });
      invalidar();
      toast.success(r.estado === 'publicado' ? 'Aprobado y publicado en la Biblioteca'
        : r.estado === 'aprobado' ? 'Aprobado — el worker publica el video en el próximo ciclo' : 'Rechazado');
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const eliminarFuente = async (f: Fuente) => {
    if (!(await dlg.confirmar(`¿Eliminar la carpeta "${f.nombre}"?`, { peligro: true }))) return;
    void sigUpdate('buzon_fuente', f.id, { deleted_at: new Date().toISOString(), activo: false })
      .then(invalidar).then(() => toast.success('Fuente eliminada')).catch((e) => toast.error(getErrorMessage(e)));
  };

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      {dlg.ui}
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Inbox className="w-5 h-5" /> Buzón externo</h1>
        <Button className="ml-auto" onClick={() => setEdit({ proveedor: 'gdrive', intervalo_min: 10, activo: true })}><Plus className="w-4 h-4 mr-1" /> Carpeta</Button>
      </div>
      <p className="text-sm text-muted-foreground">
        La agencia o el laboratorio sube archivos a su carpeta de Drive/Dropbox y aparecen aquí para
        <strong> moderación</strong> — nada llega a las pantallas sin aprobación. Imagen aprobada → Biblioteca al
        instante; video aprobado → se publica en el próximo ciclo del worker.
      </p>

      {/* Fuentes */}
      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-muted-foreground border-b bg-muted/30">
            <th className="py-2 px-3 text-right w-10">N°</th><th className="py-2 px-3 text-left">Carpeta</th>
            <th className="py-2 px-3 text-center">Proveedor</th><th className="py-2 px-3 text-left">Última sync</th>
            <th className="py-2 px-3 text-center">Activa</th><th className="py-2 px-3" />
          </tr></thead>
          <tbody>
            {fuentes.map((f, i) => (
              <tr key={f.id} className="border-b last:border-0">
                <td className="py-2 px-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 px-3">
                  <span className="font-medium">{f.nombre}</span>
                  <span className="text-muted-foreground text-xs"> — {f.carpeta}</span>
                  {f.ultimo_error && <div className="text-[11px] text-amber-600 dark:text-amber-400" title={f.ultimo_error}>⚠ {f.ultimo_error.slice(0, 70)}</div>}
                </td>
                <td className="py-2 px-3 text-center">{PROV[f.proveedor]}</td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{f.ultima_sync ? new Date(f.ultima_sync).toLocaleString(LOCALE) : 'pendiente'}</td>
                <td className="py-2 px-3 text-center">{f.activo ? 'Sí' : 'No'}</td>
                <td className="py-2 px-3 text-right whitespace-nowrap">
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={f.credencial ? 'Credencial cargada — reemplazar' : 'Cargar credencial'}
                    onClick={() => { setCredDe(f); setCred(''); }}>
                    <KeyRound className={`w-4 h-4 ${f.credencial ? 'text-green-600' : 'text-amber-500'}`} />
                  </button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEdit(f)}><Pencil className="w-4 h-4" /></button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Eliminar" onClick={() => eliminarFuente(f)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
            {!fuentes.length && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin carpetas. Agrega la de tu agencia o proveedor.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Items en moderación */}
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold">Archivos recibidos</h2>
        <select className={`${fieldCls} w-auto text-xs ml-auto`} value={filtro} onChange={(e) => setFiltro(e.target.value)}>
          <option value="pendiente">Pendientes de moderar</option>
          <option value="aprobado">Aprobados (video en cola)</option>
          <option value="publicado">Publicados</option>
          <option value="rechazado">Rechazados</option>
          <option value="error">Con error</option>
          <option value="">Todos</option>
        </select>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => {
          const f = fuentes.find((x) => x.id === it.fuente_id);
          return (
            <div key={it.id} className="border rounded-lg overflow-hidden">
              {it.tipo === 'imagen' && it.datos
                ? <img src={it.datos} alt="" className="w-full h-36 object-cover" />
                : <div className="w-full h-36 bg-muted flex flex-col items-center justify-center gap-1 text-muted-foreground">
                    {it.tipo === 'video' ? <FileVideo className="w-8 h-8" /> : <ImageIcon className="w-8 h-8" />}
                    <span className="text-[11px]">{it.tipo === 'video' ? 'Video (preview al publicar)' : 'Imagen'}</span>
                  </div>}
              <div className="p-2.5 space-y-1.5 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium truncate flex-1" title={it.nombre}>{it.nombre}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${ESTADO_CLS[it.estado] ?? ''}`}>{it.estado}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span>{f?.nombre ?? '—'}</span>
                  {it.bytes != null && <span>{(it.bytes / 1024 / 1024).toFixed(1)} MB</span>}
                  <span className="ml-auto">{new Date(it.created_at).toLocaleDateString(LOCALE)}</span>
                </div>
                {it.error && <p className="text-red-500">{it.error}</p>}
                {it.estado === 'pendiente' && (
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => void decidir(it, true)}
                      className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-green-600 hover:bg-green-700 text-white font-medium py-1">
                      <Check className="w-3.5 h-3.5" /> Aprobar
                    </button>
                    <button onClick={() => void decidir(it, false)}
                      className="flex-1 inline-flex items-center justify-center gap-1 rounded-md border border-border text-muted-foreground hover:bg-muted py-1">
                      <X className="w-3.5 h-3.5" /> Rechazar
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {!items.length && <div className="col-span-full py-8 text-center text-sm text-muted-foreground">Nada por aquí{filtro === 'pendiente' ? ' — sin archivos pendientes de moderar' : ''}.</div>}
      </div>

      {/* Modal fuente */}
      {edit && (
        <Modal title={edit.id ? 'Editar carpeta' : 'Nueva carpeta vigilada'} onClose={() => setEdit(null)}>
          <div className="space-y-3">
            <Campo label="Proveedor">
              <select className={fieldCls} value={edit.proveedor ?? 'gdrive'} onChange={(e) => setEdit({ ...edit, proveedor: e.target.value as Fuente['proveedor'] })}>
                <option value="gdrive">Google Drive</option>
                <option value="dropbox">Dropbox</option>
              </select>
            </Campo>
            <Campo label="Nombre"><input className={fieldCls} placeholder="Carpeta agencia XYZ" value={edit.nombre ?? ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} /></Campo>
            <Campo label={edit.proveedor === 'dropbox' ? 'Ruta de la carpeta (ej. /signage)' : 'ID de la carpeta de Drive (el tramo final de su URL)'}>
              <input className={fieldCls} value={edit.carpeta ?? ''} onChange={(e) => setEdit({ ...edit, carpeta: e.target.value })} />
            </Campo>
            <Campo label="Revisar cada (min)"><input type="number" min={2} max={1440} className={`${fieldCls} w-28`} value={edit.intervalo_min ?? 10} onChange={(e) => setEdit({ ...edit, intervalo_min: Number(e.target.value) })} /></Campo>
            {edit.proveedor === 'gdrive'
              ? <p className="text-xs text-muted-foreground">La credencial (🔑) es el JSON del <strong>service account</strong> de Google; comparte la carpeta con el email del service account como Lector.</p>
              : <p className="text-xs text-muted-foreground">La credencial (🔑) es un JSON <code>{'{"app_key","app_secret","refresh_token"}'}</code> de tu app de Dropbox (OAuth offline).</p>}
            <Switch checked={edit.activo ?? true} onChange={(v) => setEdit({ ...edit, activo: v })} label="Carpeta activa" />
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button><Button onClick={() => void guardarFuente()} isLoading={guardando}>Guardar</Button></div>
          </div>
        </Modal>
      )}

      {/* Modal credencial */}
      {credDe && (
        <Modal title={`Credencial · ${credDe.nombre}`} onClose={() => setCredDe(null)}>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Se guarda <strong>cifrada</strong>; solo el worker la usa para leer la carpeta.
              {credDe.proveedor === 'gdrive' ? ' Pega el JSON completo del service account.' : ' Pega el JSON {"app_key","app_secret","refresh_token"}.'}
            </p>
            <textarea className={`${fieldCls} h-40 font-mono text-xs`} value={cred} onChange={(e) => setCred(e.target.value)} placeholder='{ ... }' />
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setCredDe(null)}>Cancelar</Button><Button onClick={() => void guardarCred()} isLoading={guardando} disabled={!cred.trim()}>Guardar credencial</Button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
